import React from 'react';
import styled from 'styled-components';
import {
  CaptionText,
  FlexBox,
  FlexRow,
  SvgIcon,
  SvgIconSocial,
} from '@make-software/cspr-design';
import { BodyText } from '@block-explorer/ui';

import {
  aboutCSPRLive,
  aboutMake,
  csprLiveSocialChannels,
  aboutCSPRSuite,
  makeSocialChannels,
  tosLinks,
} from './page-footer-config';
import { useTranslation } from 'react-i18next';
import { useGetStatusMetrics } from '../../hooks/use-get-status-metrics';

const StyledFooter = styled.footer(({ theme }) => ({
  width: '100%',
}));

export const ContentWrapperTop = styled(FlexBox)(({ theme }) =>
  theme.withMedia({
    background: theme.styleguideColors.backgroundTertiary,
    width: '100%',
    flexDirection: ['row'],
    justifyContent: 'center',
  })
);

export const ContentWrapperBottom = styled(FlexBox)(({ theme }) =>
  theme.withMedia({
    background: theme.styleguideColors.backgroundQuaternary,
    width: '100%',
    flexDirection: ['row'],
    justifyContent: 'center',
  })
);

export const FooterTopBar = styled(FlexBox)(({ theme }) =>
  theme.withMedia({
    maxWidth: 1176,
    width: '100%',
    flexDirection: ['column', 'row'],
    padding: ['48px 28px 60px', '48px 32px 56px', '60px 56px', '60px 0px'],
    '> * + *': {
      marginTop: [60, 0],
      marginLeft: [0, 16, 20, 24],
    },
  })
);

export const FooterBottomBar = styled(FlexBox)(({ theme }) =>
  theme.withMedia({
    maxWidth: 1176,
    width: '100%',
    flexDirection: ['column', 'row'],
    padding: ['24px 28px', '24px 32px', '24px 56px', '24px 0px'],
    '> * + *': {
      marginTop: [32, 0],
      marginLeft: [0, 16, 20, 24],
    },
  })
);

export const FooterBarColumn = styled(FlexBox)(({ theme }) =>
  theme.withMedia({
    flexDirection: ['column'],
    width: '100%',
  })
);

export const FooterLinksSection = styled(FlexBox)(({ theme }) =>
  theme.withMedia({
    flexDirection: ['column'],
    width: '100%',
  })
);

export const FooterLinksSectionLogo = styled(FlexBox)(({ theme }) =>
  theme.withMedia({
    width: '100%',
    marginBottom: 60,
    height: 48,
  })
);

export const FooterLinksSectionTitle = styled(BodyText)(({ theme }) =>
  theme.withMedia({
    marginBottom: 16,
    color: theme.styleguideColors.contentTertiary,
  })
);

export const FooterLinkSectionItem = styled.a(({ theme }) =>
  theme.withMedia({
    marginBottom: 8,
    color: theme.styleguideColors.contentOnFill,
    cursor: 'pointer',
    ':hover': {
      fontWeight: theme.typography.fontWeight.semiBold,
      color: theme.styleguideColors.contentTertiary,
    },
    lineHeight: '20px',
  })
);

export const FooterSocialsSection = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    width: [260, 195, 195, '100%'],
    flexWrap: 'wrap',
    marginTop: 32,
    height: 80,
  })
);

const SocialMediaIconContainer = styled(FlexRow)(({ theme }) =>
  theme.withMedia({
    width: '32px',
    height: '32px',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: [20, 16, 16, 16],
  })
);

export const StyledSocialMediaIcon = styled(SvgIconSocial)(({ theme }) =>
  theme.withMedia({
    color: theme.styleguideColors.contentOnFill,
    position: 'relative',
    ':after': {
      content: "''",
      position: 'absolute',
      left: '-8px',
      bottom: '-4px',
      width: '32px',
      height: '32px',
      borderRadius: '16px',
      backgroundColor: theme.styleguideColors.contentOnFill,
      opacity: 0.08,
    },
  })
);

export const DeveloperHubIcon = styled.a(({ theme }) =>
  theme.withMedia({
    display: 'flex',
    height: '32px',
    width: '141px',
    padding: '6px 12px',
    alignItems: 'center',
    color: theme.styleguideColors.contentOnFill,
    borderRadius: '16px',
    backgroundColor: `${theme.styleguideColors.contentOnFill}14`,
    marginTop: 32,
  })
);

const MakeLogoBlackAndWhite = styled(SvgIcon)(({ theme }) =>
  theme.withMedia({
    marginTop: 8,
    'svg path': {
      fill: theme.styleguideColors.contentOnFill,
    },
  })
);

const renderFooterLinkSectionItems = (
  data: { label: string; url: string; target?: string }[]
) =>
  data.map((item, index) => (
    <FooterLinkSectionItem
      target={item.target ? item.target : '_blank'}
      href={item.url}
      rel="noopener"
      key={item.label + index}
    >
      <BodyText size={3}>{item.label}</BodyText>
    </FooterLinkSectionItem>
  ));

const renderFooterSocialsSectionItems = (data) =>
  data.map((social, index) => (
    <SocialMediaIconContainer key={social.label + index}>
      <StyledSocialMediaIcon
        key={social.key + 'icon'}
        socialMediaType={social.key}
        userId={social.urlUserId}
      />
    </SocialMediaIconContainer>
  ));

const PageFooter = () => {
  const { t } = useTranslation();
  const { data } = useGetStatusMetrics();
  const shouldShowCommitHash = ['dev', 'stg'].includes(config.environment_name);

  return (
    <StyledFooter id={'footer'} role="footer" tabIndex={-1}>
      <ContentWrapperTop>
        <FooterTopBar>
          <FooterBarColumn>
            <FooterLinksSection>
              <FooterLinksSectionLogo>
                <img
                  src="/assets/icons/logos/cspr-live-full.svg"
                  alt={t('CSPR.LIVE logo')}
                  width={107}
                  height={48}
                />
              </FooterLinksSectionLogo>
              <FooterLinksSectionTitle size={1}>
                {t('About CSPR.live')}
              </FooterLinksSectionTitle>
              {renderFooterLinkSectionItems(aboutCSPRLive(t))}
            </FooterLinksSection>
            <FooterSocialsSection>
              {renderFooterSocialsSectionItems(csprLiveSocialChannels(t))}
            </FooterSocialsSection>
          </FooterBarColumn>
          <FooterBarColumn>
            <FooterLinksSection>
              <FooterLinksSectionLogo>
                <SvgIcon
                  role={'img'}
                  src="/assets/icons/logos/make-full.svg"
                  alt={t('MAKE company Logo')}
                  width={158}
                  height={48}
                />
              </FooterLinksSectionLogo>
              <FooterLinksSectionTitle size={1}>
                {t('About MAKE')}
              </FooterLinksSectionTitle>
              {renderFooterLinkSectionItems(aboutMake(t))}
            </FooterLinksSection>
            <FooterSocialsSection>
              {renderFooterSocialsSectionItems(makeSocialChannels(t))}
            </FooterSocialsSection>
          </FooterBarColumn>
          <FooterBarColumn>
            <FooterLinksSection>
              <FooterLinksSectionLogo>
                <SvgIcon
                  role={'img'}
                  src="/assets/icons/logos/cspr-suite.svg"
                  alt={t('CSPR Suite Logo')}
                  width={228}
                  height={40}
                />
              </FooterLinksSectionLogo>
              <FooterLinksSectionTitle size={1}>
                {t('CSPR Suite')}
              </FooterLinksSectionTitle>
              {renderFooterLinkSectionItems(aboutCSPRSuite(t))}
            </FooterLinksSection>
            <DeveloperHubIcon
              href={'https://cspr.build/'}
              target="_blank"
              rel="noopener"
              color="hash"
            >
              <BodyText size={3} variation={'white'}>
                {'</>'} Developer Hub
              </BodyText>
            </DeveloperHubIcon>
          </FooterBarColumn>
        </FooterTopBar>
      </ContentWrapperTop>

      <ContentWrapperBottom>
        <FooterBottomBar>
          <FooterBarColumn>
            <BodyText variation="gray" size={3}>
              Powered by
            </BodyText>
            <MakeLogoBlackAndWhite
              src="/assets/icons/logos/make-full.svg"
              alt={t('MAKE company logo')}
              width={66}
              height={20}
              role={'img'}
            />
          </FooterBarColumn>
          <FooterBarColumn>
            {renderFooterLinkSectionItems(tosLinks(t))}
          </FooterBarColumn>
          {data && (
            <FooterBarColumn>
              <CaptionText variation="gray" size={2}>
                CSPR.live version: {data.casperLiveVersion}{' '}
                {shouldShowCommitHash && ` (${data.casperLiveCommit})`}
              </CaptionText>
              <CaptionText variation="gray" size={2}>
                Casper Network Node version: {data.casperNetworkVersion}{' '}
                {shouldShowCommitHash && ` (${data.casperNetworkCommit})`}
              </CaptionText>
            </FooterBarColumn>
          )}
        </FooterBottomBar>
      </ContentWrapperBottom>
    </StyledFooter>
  );
};

export default PageFooter;
